import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Form, Input, Typography, Button } from 'antd';
// Api
import { UPDATE_SOCIAL_SHARE_CAMPAIGN } from 'api/campaignV2/mutations';
import { SEARCH_SOCIAL_SHARE_CAMPAIGN_STORES } from 'api/campaignV2/queries';
// Helpers
import { getSocialShareActivity } from 'helpers/cbActivity';
import {
  FOLLOWING_OPTIONS_MAX,
  FOLLOWING_OPTIONS_MIN,
  getLocationsForApi,
} from 'helpers/campaignEditModal';
import { getSocialShareCampaignCommonFields } from 'helpers/campaignSocialShare';
import {
  convertEngagementRateToOption,
  getEngagementRateValue,
} from 'helpers/campaign';
// Types
import {
  GetCampaignsV2_getCampaigns_entities,
  GetCampaignsV2_getCampaigns_entities_search,
} from 'api/campaignV2/types/GetCampaignsV2';
import {
  CampaignActivityType,
  CampaignLocationInput,
  CampaignSmPlatform,
  CampaignTaskType,
  SearchCampaignCriteriaInput,
  SearchSocialShareCriteriaInput,
  SocialShareActivityInput,
  SortDirection,
  StoreGender,
  UpdateSocialShareCampaignInput,
  UserRole,
  AudienceAgeRange,
  AudienceEthnicity,
  AudienceGender,
  AudienceInterest,
} from 'api/graphql-global-types';
import {
  SearchSocialShareCampaignStores,
  SearchSocialShareCampaignStoresVariables,
} from 'api/campaignV2/types/SearchSocialShareCampaignStores';
import {
  UpdateSocialShareCampaign,
  UpdateSocialShareCampaignVariables,
} from 'api/campaignV2/types/UpdateSocialShareCampaign';
// Hooks
import { useGetMyEntitlements } from 'hooks';
// Helpers
import { canEditPremiumFilters } from 'helpers/brands';
// Ui
import { errorNotification, successNotification } from 'ui/Notification';
// Components
import CampaignCriteria from 'components/common/CampaignEditComponents/CampaignCriteria/CampaignCriteria';
import CampaignDetails from 'components/common/CampaignEditComponents/CampaignDetails/CampaignDetails';
import CompanyDetails from 'components/common/CampaignEditComponents/CompanyDetails/CompanyDetails';
// Style
import styles from './SocialShareCampaignEditModal.module.scss';

const { Text } = Typography;
const { TextArea } = Input;

export type SocialShareCampaignFormValues = {
  userRole: UserRole[];
  budget: number;
  direction?: SortDirection | null;
  flatFeePerStore: number;
  genders?: StoreGender[] | null;
  locations?: CampaignLocationInput[] | null;
  maxAge?: number | null;
  minAge?: number | null;
  smPlatforms: CampaignSmPlatform[];
  followingFrom: number | null;
  followingTo: number | null;
  sports: string[];
  brandName: string;
  website: string;
  description: string;
  campaignName: string;
  internalName: string;
  campaignInstructions: string;
  dueDate: any;
  applicationDeadlineDays?: number | null;
  applicationDeadlineEnabled?: boolean;
  shareUrl: string;
  hometownLocations?: CampaignLocationInput[] | null;
  excludedLocations?: CampaignLocationInput[] | null;
  engagementRate?: { value: number; label: string } | null;
  searchHashtags?:
    | { label: string; value: string; storeCount: number }[]
    | null;
  audienceGender?: AudienceGender | null;
  audienceAgeRanges?: AudienceAgeRange[] | null;
  audienceEthnicities?: AudienceEthnicity[] | null;
  audienceLocations?: CampaignLocationInput[] | null;
  audienceInterests?: AudienceInterest[] | null;
};

type SocialShareCampaignEditModalProps = {
  record: GetCampaignsV2_getCampaigns_entities;
  onFinish: () => void;
};

const SocialShareCampaignEditModal = ({
  record,
  onFinish,
}: SocialShareCampaignEditModalProps): JSX.Element | null => {
  const [form] = Form.useForm();

  const ownerId = record?.agency?.id;
  const { data: entitlementsData } = useGetMyEntitlements({
    ownerId,
    skip: !ownerId,
  });
  const canEditPremiumFiltersValue = canEditPremiumFilters(
    entitlementsData?.getMyEntitlements
  );

  const activity = getSocialShareActivity(record);

  const [selectedTasks, setSelectedTasks] = useState<CampaignTaskType[]>(
    activity?.tasks.map((item) => {
      return item.type;
    })
  );

  const [isInstagram, setIsInstagram] = useState<boolean>(
    selectedTasks?.every((item) => {
      return item === CampaignTaskType.IG_SHARE;
    })
  );

  const [isTwitter, setIsTwitter] = useState<boolean>(
    selectedTasks?.every((item) => {
      return item === CampaignTaskType.TW_SHARE;
    })
  );

  const [isTikTok, setIsTikTok] = useState<boolean>(
    selectedTasks?.every((item) => {
      return item === CampaignTaskType.TT_SHARE;
    })
  );

  const filters = record?.search as GetCampaignsV2_getCampaigns_entities_search;

  const [
    searchSocialShareStores,
    { data: storeData, loading: loadingStoreData },
  ] = useLazyQuery<
    SearchSocialShareCampaignStores,
    SearchSocialShareCampaignStoresVariables
  >(SEARCH_SOCIAL_SHARE_CAMPAIGN_STORES);

  const [updateSocialShareCampaign, { loading: loadingUpdateCampaign }] =
    useMutation<UpdateSocialShareCampaign, UpdateSocialShareCampaignVariables>(
      UPDATE_SOCIAL_SHARE_CAMPAIGN
    );

  const initialFormValues: SocialShareCampaignFormValues = {
    userRole: filters?.roles || [],
    budget: filters?.budget || 0,
    flatFeePerStore: record?.flatFeePerStore || 0,
    genders: filters?.genders || [],
    maxAge: filters?.athleteMaxAge || null,
    minAge: filters?.athleteMinAge || null,
    smPlatforms: filters?.smPlatforms || [],
    followingFrom:
      FOLLOWING_OPTIONS_MIN.find((f) => f.value === filters.minFollowers)
        ?.value || null,
    followingTo:
      FOLLOWING_OPTIONS_MAX.find((f) => f.value === filters.maxFollowers)
        ?.value || null,
    sports: filters?.sports || [],
    brandName: activity?.brandName || '',
    website: activity?.website || '',
    description: activity?.socialShareDescription || '',
    campaignName: record?.name || '',
    internalName: record?.internalName || '',
    campaignInstructions: activity?.instructions || '',
    dueDate: record?.dueDate ? moment(record?.dueDate) : null,
    applicationDeadlineDays: record?.applicationDeadlineDays || null,
    applicationDeadlineEnabled: record?.applicationDeadlineEnabled ?? true,
    shareUrl: activity?.shareUrl || '',
    locations: record?.search?.locations || [],
    hometownLocations: record?.search?.hometownLocations || [],
    excludedLocations: record?.search?.excludedLocations || [],
    engagementRate: convertEngagementRateToOption(
      record?.search?.engagementRateFrom
    ),
    searchHashtags: record?.search?.hashtags
      ? record.search.hashtags.map((tag, idx) => ({
          label: tag,
          value: String(idx),
          storeCount: 0,
        }))
      : [],
    audienceGender: filters?.audienceGender || null,
    audienceAgeRanges: filters?.audienceAgeRanges || [],
    audienceEthnicities: filters?.audienceEthnicities || [],
    audienceLocations: filters?.audienceLocations || [],
    audienceInterests: filters?.audienceInterests || [],
  };

  // Set initial form values when record changes (only reset when editing a different campaign)
  useEffect(() => {
    if (record && filters) {
      form.setFieldsValue({
        campaignName: record?.name || '',
        internalName: record?.internalName || '',
        brandName: activity?.brandName || '',
        website: activity?.website || '',
        description: activity?.socialShareDescription || '',
        campaignInstructions: activity?.instructions || '',
        dueDate: record?.dueDate ? moment(record?.dueDate) : null,
        applicationDeadlineDays: record?.applicationDeadlineDays || null,
        applicationDeadlineEnabled: record?.applicationDeadlineEnabled ?? true,
        shareUrl: activity?.shareUrl || '',
        userRole: filters?.roles || [],
        budget: filters?.budget || 0,
        flatFeePerStore: record?.flatFeePerStore || 0,
        genders: filters?.genders || [],
        maxAge: filters?.athleteMaxAge || null,
        minAge: filters?.athleteMinAge || null,
        followingFrom:
          FOLLOWING_OPTIONS_MIN.find((f) => f.value === filters?.minFollowers)
            ?.value || null,
        followingTo:
          FOLLOWING_OPTIONS_MAX.find((f) => f.value === filters?.maxFollowers)
            ?.value || null,
        sports: filters?.sports || [],
        locations: record?.search?.locations || [],
        hometownLocations: record?.search?.hometownLocations || [],
        excludedLocations: record?.search?.excludedLocations || [],
        engagementRate: convertEngagementRateToOption(
          record?.search?.engagementRateFrom
        ),
        searchHashtags: record?.search?.hashtags
          ? record.search.hashtags.map((tag, idx) => ({
              label: tag,
              value: String(idx),
              storeCount: 0,
            }))
          : [],
        audienceGender: filters?.audienceGender || null,
        audienceAgeRanges: filters?.audienceAgeRanges || [],
        audienceEthnicities: filters?.audienceEthnicities || [],
        audienceLocations: filters?.audienceLocations || [],
        audienceInterests: filters?.audienceInterests || [],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record?.id]);

  const handleSubmit = async () => {
    const {
      brandName,
      website,
      description,
      dueDate,
      campaignInstructions,
      campaignName,
      internalName,
      shareUrl,
      locations,
      hometownLocations,
      engagementRate,
      searchHashtags,
      applicationDeadlineDays,
      applicationDeadlineEnabled,
    } = form.getFieldsValue();

    const commonFields = getSocialShareCampaignCommonFields(
      form.getFieldsValue(),
      isTikTok
    );
    const searchLocations = getLocationsForApi(locations);
    const searchHometownLocations = getLocationsForApi(hometownLocations || []);
    const searchExcludedLocations = getLocationsForApi(
      form.getFieldValue('excludedLocations') || []
    );

    const criteria: SearchSocialShareCriteriaInput = {
      ...commonFields,
      ...(searchLocations.length && { locations: searchLocations }),
      ...(searchHometownLocations.length && {
        hometownLocations: searchHometownLocations,
      }),
      ...(searchExcludedLocations.length && {
        excludedLocations: searchExcludedLocations,
      }),
      ...(() => {
        const engagementRateValue = getEngagementRateValue(engagementRate);
        return engagementRateValue !== null
          ? {
              engagementRateRange: {
                from: engagementRateValue,
                to: 1,
              },
            }
          : {};
      })(),
      ...(searchHashtags && {
        hashtagInputs: searchHashtags.map((tag: { label: string }) => ({
          name: tag.label.trim(),
        })),
      }),
      smPlatforms: isInstagram
        ? [CampaignSmPlatform.IG]
        : isTwitter
        ? [CampaignSmPlatform.TW]
        : [CampaignSmPlatform.TT],
    };

    const socialShare = {
      ...(brandName && { brandName }),
      ...(campaignInstructions && { instructions: campaignInstructions }),
      ...(website && { website }),
      ...(shareUrl && { shareUrl }),
      ...(description && { description }),
    };

    const activities: SocialShareActivityInput[] = [];

    if (Object.keys(socialShare).length > 0) {
      activities.push({
        socialShare,
        type: CampaignActivityType.SocialShare,
      });
    }

    const updateCampaignInputVariables: UpdateSocialShareCampaignInput = {
      id: record?.id || '',
      ownerId: record?.agency.id,
      criteria,
      ...(activities.length > 0 && { activities }),
      ...(campaignName && { name: campaignName }),
      internalName: internalName || campaignName,
      ...(dueDate && { dueDate: dueDate }),
      lockEnabled: true,
      ...(applicationDeadlineDays && { applicationDeadlineDays }),
      ...(applicationDeadlineEnabled !== undefined && {
        applicationDeadlineEnabled,
      }),
    };

    try {
      const { data } = await updateSocialShareCampaign({
        variables: {
          input: updateCampaignInputVariables,
        },
      });
      if (data?.updateSocialShareCampaign) {
        successNotification('The campaign was successfully updated');
        onFinish();
      }
    } catch (error) {
      errorNotification((error as Error)?.message);
      console.error('Campaign update error:', { error });
    }
  };

  const handleFilterSearch = async (criteria: SearchCampaignCriteriaInput) => {
    try {
      searchSocialShareStores({
        variables: {
          input: {
            limit: 4,
            offset: 0,
            criteria,
          },
        },
      });
    } catch (error) {
      errorNotification((error as Error)?.message);
      console.error('Campaign filter error:', { error });
    }
  };

  const handleFilterClick = async () => {
    const commonFields = getSocialShareCampaignCommonFields(
      form.getFieldsValue(),
      isTikTok
    );
    const searchLocations = getLocationsForApi(commonFields?.locations || []);
    const searchAudienceLocations = getLocationsForApi(
      commonFields.audienceFilters?.locations || []
    );
    // Get hashtags and engagementRate from the form
    const searchHashtags = form.getFieldValue('searchHashtags');
    const engagementRate = form.getFieldValue('engagementRate');
    const hometownLocations = form.getFieldValue('hometownLocations');
    const searchHometownLocations = getLocationsForApi(hometownLocations || []);
    const searchExcludedLocations = getLocationsForApi(
      form.getFieldValue('excludedLocations') || []
    );

    const criteria: SearchCampaignCriteriaInput = {
      ...commonFields,
      ...(searchLocations.length && { locations: searchLocations }),
      ...(searchAudienceLocations.length && {
        audienceFilters: {
          ...commonFields.audienceFilters,
          locations: searchAudienceLocations,
        },
      }),
      ...(searchHometownLocations.length && {
        hometownLocations: searchHometownLocations,
      }),
      ...(searchExcludedLocations.length && {
        excludedLocations: searchExcludedLocations,
      }),
      ...(() => {
        const engagementRateValue = getEngagementRateValue(engagementRate);
        return engagementRateValue !== null
          ? {
              engagementRateRange: {
                from: engagementRateValue,
                to: 1,
              },
            }
          : {};
      })(),
      ...(searchHashtags && {
        hashtagInputs: searchHashtags.map((tag: { label: string }) => ({
          name: tag.label.trim(),
        })),
      }),
      smPlatforms: [
        isInstagram
          ? CampaignSmPlatform.IG
          : isTwitter
          ? CampaignSmPlatform.TW
          : CampaignSmPlatform.TT,
      ],
    };

    handleFilterSearch(criteria);
  };

  const talentsData = storeData?.searchSocialShareCampaignStores?.entities;

  const hasMore =
    (talentsData?.length ?? 0) <
    (storeData?.searchSocialShareCampaignStores.total ?? 0);

  const showTalentList =
    !!talentsData?.length && talentsData?.length > 0 && !loadingStoreData;

  const totalFollowers = Number(
    storeData?.searchSocialShareCampaignStores.summary.followers
  );
  const totalTalents = Number(storeData?.searchSocialShareCampaignStores.total);

  useEffect(() => {
    // Used to call the filters API and display the initial talents
    const commonFields = getSocialShareCampaignCommonFields(
      initialFormValues,
      isTikTok
    );
    const searchLocations = getLocationsForApi(commonFields?.locations || []);

    const criteria: SearchCampaignCriteriaInput = {
      ...commonFields,
      ...(searchLocations.length && { locations: searchLocations }),
      smPlatforms: initialFormValues.smPlatforms,
    };

    handleFilterSearch(criteria);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitLoading = loadingUpdateCampaign || loadingStoreData;

  if (!record) return null;

  return (
    <div className={styles.root}>
      <Form
        name="EditCampaignForm"
        aria-label="Update Social Campaign"
        layout="vertical"
        onFinish={handleSubmit}
        form={form}
        initialValues={initialFormValues}
      >
        <div className={styles.columnWrapper}>
          <CampaignCriteria
            form={form}
            initialFormValues={initialFormValues}
            record={record}
            isInstagram={isInstagram}
            setIsInstagram={setIsInstagram}
            isTwitter={isTwitter}
            setIsTwitter={setIsTwitter}
            isTikTok={isTikTok}
            setIsTikTok={setIsTikTok}
            setSelectedTasks={setSelectedTasks}
            handleFilterClick={handleFilterClick}
            loadingStoreData={loadingStoreData}
            talentsData={talentsData}
            hasMore={hasMore}
            totalTalents={totalTalents}
            totalFollowers={totalFollowers}
            showTalentList={showTalentList}
            canEditPremiumFilters={canEditPremiumFiltersValue}
          />
          <CampaignDetails
            record={record}
            showInstructions={false}
            renderCustomFields={() => (
              <div>
                <Form.Item
                  name="campaignInstructions"
                  rules={[
                    {
                      required: true,
                      message: 'This field is required',
                    },
                  ]}
                  label={<Text>Campaign instructions</Text>}
                >
                  <TextArea
                    autoSize={{ minRows: 3 }}
                    name="campaignInstructions"
                    placeholder="Enter detailed instructions. Let talent know where you want them to share your post to and when. Include any links or copy you want them including on the share."
                  />
                </Form.Item>
              </div>
            )}
          />

          <CompanyDetails
            renderCustomFields={() => (
              <div>
                <Form.Item
                  name="description"
                  rules={[
                    {
                      required: true,
                      message: 'This field is required',
                    },
                  ]}
                  label={<Text>Description</Text>}
                >
                  <TextArea
                    autoSize={{ minRows: 3 }}
                    name="description"
                    placeholder="Enter Company/Brand product/service description"
                  />
                </Form.Item>
              </div>
            )}
          />

          <div className={styles.heading}>Post Details:</div>

          <div className={styles.rowWrapper}>
            <Form.Item
              name="shareUrl"
              rules={[
                {
                  required: true,
                  message: 'This field is required',
                },
              ]}
              label={<Text>Post Link</Text>}
            >
              <Input
                type="text"
                placeholder="https://example.link"
                aria-label="https://example.link"
              />
            </Form.Item>
          </div>
        </div>
        <div className={styles.submitButtonWrapper}>
          <Button
            style={{ marginRight: 8 }}
            type="primary"
            loading={submitLoading}
            onClick={handleSubmit}
          >
            Save as Draft
          </Button>
          <Button type="primary" htmlType="submit" loading={submitLoading}>
            Submit
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default SocialShareCampaignEditModal;
